import { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  MdOutlineExitToApp,
  MdDashboard,
  MdAdminPanelSettings,
} from 'react-icons/md';
import { LuLayoutDashboard } from 'react-icons/lu';
import { useAuth } from '../../context/AuthContext';
import { ROLE } from '../../Role';
import { Avatar, useDisclosure } from '@heroui/react';
import { GrUserSettings } from 'react-icons/gr';
import BecomeProviderModal from './common/ui/BecomeProviderModal';
import { handleLogout } from './auth/apiAuth';

export const HeaderAuthMobileDropdown = () => {
  const [isOpen, setIsOpen] = useState(false);

  const dropdownRef = useRef<HTMLDivElement>(null);
  const {
    user,
    role,
    isAuthenticated,
    customerAccountStatus,
    providerAccountStatus,
    adminAccountStatus,
    profilePicture,
  } = useAuth();
  const navigate = useNavigate();

  if (!isAuthenticated) return null;

  const {
    isOpen: isModalOpen,
    onOpen: onModalOpen,
    onOpenChange: onModalOpenChange,
  } = useDisclosure();

  const showBecomeProvider = isAuthenticated && role === ROLE.CUSTOMER;

  const isValidCustomer =
    (role === ROLE.CUSTOMER || role === ROLE.PROVIDER) &&
    customerAccountStatus &&
    ['active', 'inactive'].includes(customerAccountStatus);

  const isValidProvider =
    role === ROLE.PROVIDER &&
    providerAccountStatus &&
    ['active', 'inactive'].includes(providerAccountStatus);

  const isValidAdmin =
    role === ROLE.ADMIN &&
    adminAccountStatus &&
    ['active'].includes(adminAccountStatus);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);

    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const logout = () => {
    handleLogout();
  };

  return (
    <div
      className="relative inline-block text-left"
      ref={dropdownRef}
      onClick={() => setIsOpen((prev) => !prev)}
    >
      {/* TRIGGER - Reduced size for Compact UI */}
      <div className="cursor-pointer p-1">
        <Avatar
          className="w-6 h-6 text-tiny"
          src={profilePicture || undefined}
        />
      </div>

      {/* DROPDOWN MENU */}
      {isOpen && (
        <div className="absolute right-0 z-50 pt-1 w-56 origin-top-right">
          {/* Menu Container - Reduced width from w-64 to w-56 */}
          <div className="rounded-lg border border-gray-100 bg-white shadow-lg ring-1 ring-black ring-opacity-5 overflow-hidden">
            {/* User Header - More Compact */}
            <div className="px-3 py-2 bg-gray-50/50 border-b border-gray-100 text-start">
              <p className="text-[10px] font-bold text-gray-400 uppercase tracking-tight">
                Account
              </p>
              <p className="text-xs font-semibold text-gray-700 truncate">
                {user?.email}
              </p>
            </div>

            <div className="py-1">
              {showBecomeProvider && (
                <div className="px-2 py-2">
                  {' '}
                  {/* Wrapper for padding inside dropdown */}
                  <button
                    onClick={onModalOpen}
                    className="relative group w-full flex flex-col items-start p-3 rounded-xl 
                 bg-gradient-to-br 
                 from-primary to-primary-600 
                     hover:to-primary-500 
                 border border-primary/20 hover:border-transparent
                 transition-all duration-300 ease-in-out shadow-sm hover:shadow-md"
                    aria-label="Become a Provider"
                  >
                    <div className="flex items-center gap-2 mb-1">
                      {/* Icon changes color on hover */}
                      <div className="p-1.5 rounded-lg shadow-sm bg-white/20 transition-colors">
                        <GrUserSettings
                          className="text-white  transition-colors"
                          size={16}
                        />
                      </div>
                      <span className="text-[10px] font-bold uppercase tracking-wider text-white/80 transition-colors">
                        Opportunity
                      </span>
                    </div>

                    <div className="text-left">
                      <p className="text-xs font-bold  text-white transition-colors">
                        Become a Provider
                      </p>
                      <p className="text-[10px]  text-white/70 transition-colors mt-0.5">
                        Start earning by sharing your skills.
                      </p>
                    </div>

                    {/* Decorative arrow that appears on hover */}
                    <div className="absolute right-3 top-1/2 -translate-y-1/2 opacity-0 group-hover:opacity-100 group-hover:translate-x-1 transition-all">
                      <svg
                        className="w-4 h-4 text-white"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="9 5l7 7-7 7"
                        />
                      </svg>
                    </div>
                  </button>
                  <BecomeProviderModal
                    externalIsOpen={isModalOpen}
                    externalOnOpenChange={onModalOpenChange}
                    hideTrigger
                  />
                </div>
              )}

              {/* PORTAL SECTION */}
              <div className="px-3 py-1 text-[9px] font-bold text-gray-400 uppercase tracking-widest">
                Portals
              </div>

              {/* Customer Portal */}
              {isValidCustomer && (
                <button
                  onClick={() => {
                    navigate('/customer/customer-dashboard');
                    setIsOpen(false);
                  }}
                  className="flex items-center w-full px-3 py-2 text-xs text-gray-600 hover:bg-primary-50 hover:text-primary-600 transition-colors"
                >
                  <MdDashboard className="mr-2 text-lg text-gray-400" />
                  Customer Panel
                </button>
              )}

              {/* Provider Portal */}
              {isValidProvider && (
                <a
                  href={import.meta.env.VITE_APP_PROVIDER_PORTAL_URL}
                  target="_blank"
                  rel="noreferrer"
                  className="flex items-center w-full px-3 py-2 text-xs text-gray-600 hover:bg-primary-50 hover:text-primary-600 transition-colors"
                >
                  <LuLayoutDashboard className="mr-2 text-lg text-gray-400" />
                  Provider Portal
                </a>
              )}

              {/* Admin Portal */}
              {isValidAdmin && (
                <a
                  href={import.meta.env.VITE_APP_ADMIN_PORTAL_URL}
                  target="_blank"
                  rel="noreferrer"
                  className="flex cursor-pointer items-center w-full px-3 py-2 text-xs text-gray-600 hover:bg-primary-50 hover:text-primary-600 transition-colors"
                >
                  <MdAdminPanelSettings className="mr-2 text-lg text-gray-400" />
                  Admin Portal
                </a>
              )}
            </div>

            <div className="px-3 py-1 text-[9px] font-bold text-gray-400 uppercase tracking-widest">
              Portals
            </div>

            {/* LOGOUT - Compact padding */}
            <div className="border-t border-gray-100">
              <button
                onClick={logout}
                className="flex items-center w-full px-3 py-2 text-xs  text-red-500 hover:bg-red-50 transition-colors"
              >
                <MdOutlineExitToApp className="mr-2 text-lg" />
                Logout
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
